<template>
  <div class="contact" id="contact">
    <div class="container">
      <div class="contact__wrapper">
        <div class="contact__block">
          <h2 class="contact__title">{{ t('home.contact.title') }}</h2>
          <div class="contact__box">
            <ui-input
                :type="'text'"
                :placeholder="t('fields.name')"
                :rules="[required]"
                v-model="name"
            />
            <ui-input
                :type="'email'"
                :placeholder="t('fields.email')"
                :rules="[isEmail]"
                v-model="email"
            />
          </div>
          <div class="contact__inner">
            <ui-textarea
                :placeholder="t('fields.message')"
                :rules="[required]"
                v-model="message"
            />
            <button
                class="contact__button"
                :disabled="!isFormValid"
                @click="sendForm"
            >
              {{ t('buttons.send') }}
            </button>
          </div>
          <div class="contact__checkboxes">
            <ui-checkbox
                v-model="isAgreePolicy"
                :id="1"
            >
              <p>
                {{ t('checkboxes.accept') }}
                <router-link :to="translations.i18nRoute({ name: 'home' })" class="contact__link">
                  {{ t('docs.terms.title') }}
                </router-link>
              </p>
            </ui-checkbox>
            <ui-checkbox
                v-model="isAgreeTerms"
                :id="2"
            >
              <p>
                {{ t('checkboxes.accept') }}
                <router-link :to="translations.i18nRoute({ name: 'policy' })" class="contact__link">
                  {{ t('docs.policy.title') }}
                </router-link>
              </p>
            </ui-checkbox>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import UiInput from "@/components/ui/ui-input.vue";
import {computed, ref} from "vue";
import {isEmail, required} from "@/core/rules/textFieldRules.js";
import UiTextarea from "@/components/ui/ui-textarea.vue";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import translations from "@/core/helpers/translations.js";
import {ElNotification} from "element-plus";
import {Processes} from "@/core/api/processes.js";

const {t} = useI18n()

const name = ref('')
const email = ref('')
const message = ref('')
const isAgreePolicy = ref(false)
const isAgreeTerms = ref(false)

const isFormValid = computed(() => {
  return (
      required(name.value) === true &&
      isEmail(email.value) === true &&
      required(message.value) === true &&
      isAgreePolicy.value === true &&
      isAgreeTerms.value === true
  )
})

const sendForm = async () => {
  const res = await Processes.contact({
    name: name.value,
    email: email.value,
    subject: 'Contact form',
    message: message.value
  })

  if (res.data.contactUs.received) {
    ElNotification({
      dangerouslyUseHTMLString: true,
      message: t('popup.contactUs'),
      customClass: 'notification'
    })
  } else {
    ElNotification({
      dangerouslyUseHTMLString: true,
      message: res.errors[0].message,
      customClass: 'notification'
    })
  }
}
</script>

<style lang="scss" scoped>
.contact {
  background-image: url(@images/contactBg.png);
  background-repeat: no-repeat;
  background-size: cover;

  &__wrapper {
    padding-block: 150px;
  }

  &__block {
    width: 75%;
    margin-inline: auto;
    border-radius: 20px;
    border: 1px solid $white;
    background-color: rgba(4, 10, 10, 0.75);
    padding: 65px 100px 75px 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    box-shadow: 0 0 10px 1px #ff0000;
  }

  &__title {
    color: #F3F3F3;
    font-size: 64px;
    font-weight: 700;
  }

  &__box {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 34px;
  }

  &__inner {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 24px;
  }

  &__button {
    cursor: pointer;
    width: fit-content;
    border-radius: 10px;
    border: 2px solid #F00;
    box-shadow: 0 0 20px 0 rgba(255, 0, 0, 0.46), 0 0 5px 0 #F00, 0 0 5px 1px #F00 inset;
    padding: 11px 40px;
    background-color: transparent;
    transition: 0.2s;

    color: #F00;
    font-size: 20px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 1px;
    text-transform: uppercase;

    @include hover {
      background-color: #F00;
      color: $white;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;

      @include hover {
        background-color: transparent;
        color: #F00;
      }
    }
  }

  &__checkboxes {
    display: flex;
    align-items: center;
    gap: 100px;
  }

  &__link {
    text-decoration: underline;
  }
}

@media (max-width: 1300px) {
  .contact {
    &__block {
      width: 100%;
    }
  }
}

@media (max-width: 800px) {
  .contact {
    &__block {
      padding: 30px 50px 50px 50px;
    }
  }
}

@media (max-width: 650px) {
  .contact {
    &__title {
      font-size: 36px;
    }

    &__box {
      flex-direction: column;
    }

    &__inner {
      flex-direction: column;
    }

    &__checkboxes {
      flex-direction: column;
      gap: 25px;
    }
  }
}
</style>